import { FC, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import useIntegration from 'api/useIntegration';
import { EEventCategories,EFeatureFlag } from 'constants/enums';
import { usePortalStoreInfo } from 'hooks/usePortalStoreInfo';
import {
  PublisherIdType,
  SearchKeyType,
  useTableSearch
} from 'hooks/useTableSearch';
import { ERouteContext } from 'routes/types';
import { permissionsUtil } from 'utils/permissionsUtil';

import { TableSearchFilters } from 'components/TableSearchFilters/TableSearchFilters';
import { Flex } from 'components/ui/flex';

import PageInactiveBanner from '../IntegrationSettings/PageInactiveBanner';
import { EPublisherEventValue } from '../Settings.types';

import EventCategorySection from './EventCategorySection';
import {
  enrichAndOrderEvents,
  getEventOrderMap,
  groupEventsByCategory
} from './eventsUtils';

const EventsSettings: FC<{ context: ERouteContext }> = ({ context }) => {
  const { publisherId } = useParams();
  const { isPageInactive, portalDashboardEventsUrl } = usePortalStoreInfo();
  const { getIntegration, getPublisherEventsV1, getPublisherEventsV2 }: any =
    useIntegration(publisherId);

  const { featureFlags } = useFeatureFlags({ publisherId });

  const hasFeatureFlagEventsV2 =
    featureFlags?.[EFeatureFlag.DASHBOARD_EVENTS_V2];

  const eventOrderMap = useMemo(() => getEventOrderMap(), []);

  const orderedEventsV1 = useMemo(
    () => enrichAndOrderEvents(getPublisherEventsV1?.data, eventOrderMap),
    [getPublisherEventsV1?.data, eventOrderMap]
  );

  const orderedEventsV2 = useMemo(
    () => {
      const enrichedEvents = enrichAndOrderEvents(getPublisherEventsV2?.data, eventOrderMap);
      if (context !== ERouteContext.PORTAL) {
        return enrichedEvents.filter(event => event.category !== EEventCategories.PORTAL);
      }
      
      return enrichedEvents.filter(event => {
        if (event.category === EEventCategories.PORTAL) {
          return true;
        }
        
        if (event.category === EEventCategories.WEB_STORE) {
          const allowedWebstoreEvents = [
            EPublisherEventValue.WEBSTORE_OFFERS_LOADED,
            EPublisherEventValue.WEBSTORE_OFFERS_DISPLAYED, 
            EPublisherEventValue.WEBSTORE_STORE_DISPLAYED,
            EPublisherEventValue.WEBSTORE_PROGRESS_BAR_MISSION_COMPLETED,
            EPublisherEventValue.WEBSTORE_PROGRESS_BAR_MISSION_STARTED,
            EPublisherEventValue.WEBSTORE_PROGRESS_BAR_POINTS_GRANTED,
            EPublisherEventValue.WEBSTORE_POPUP_TRIGGERED,
            EPublisherEventValue.WEBSTORE_BADGE_CLICKED,
            EPublisherEventValue.WEBSTORE_OFFER_CLICKED
          ];
          return allowedWebstoreEvents.includes(event.value);
        }
        
        return true;
      });
    },
    [getPublisherEventsV2?.data, eventOrderMap, context]
  );

  const {
    searchValue: searchValueV1,
    filterValue: filterValueV1,
    setSearchValue: setSearchValueV1,
    setFilterValue: setFilterValueV1,
    filteredData: filteredDataV1,
    isTableDataEmpty: isTableDataEmptyV1
  } = useTableSearch({
    data: orderedEventsV1,
    publisherId: PublisherIdType.SUB_CATEGORY,
    searchKey: SearchKeyType.VALUE
  });

  const {
    searchValue: searchValueV2,
    filterValue: filterValueV2,
    setSearchValue: setSearchValueV2,
    setFilterValue: setFilterValueV2,
    filteredData: filteredDataV2,
    isTableDataEmpty: isTableDataEmptyV2
  } = useTableSearch({
    data: orderedEventsV2,
    publisherId: PublisherIdType.SUB_CATEGORY,
    searchKey: SearchKeyType.VALUE
  });

  const renderEventSections = (
    version: 'v1' | 'v2',
    events: any,
    isEmpty: boolean
  ) => {
    const categorized = groupEventsByCategory(
      events,
      permissionsUtil.isCheckout()
    );

    return Object.entries(categorized).map(([category, items]) => (
      <EventCategorySection
        key={`${version}-${category}`}
        category={category}
        events={items}
        enabledEvents={getIntegration?.data?.enabledWebhookEvents}
        enabledEventsV2={getIntegration?.data?.enabledWebhookEventsV2}
        isTableDataEmpty={isEmpty}
      />
    ));
  };

  return (
    <Flex className="gap-4">
      {isPageInactive && (
        <PageInactiveBanner
          message="Event Center settings are configured in the Portal Dashboard for stores connected to a portal."
          linkText="Portal Dashboard"
          linkUrl={portalDashboardEventsUrl}
        />
      )}
      <div className={isPageInactive ? 'opacity-50 pointer-events-none' : ''}>
      {context !== ERouteContext.PORTAL && (
        <Accordion sx={{ boxShadow: 'none', borderBottom: '1px solid #E0E0E0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>
              Events V1
            </Typography>
            {hasFeatureFlagEventsV2 && (
              <Typography sx={{ mx: 1.5 }}>
                (These events will be deprecated on 15-01-2026)
              </Typography>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1, fontSize: 14, fontFamily: 'Montserrat' }}>
              Webhook Url: {getIntegration?.data?.eventsWebhookUrl || 'https://'}
            </Typography>
            <TableSearchFilters
              searchValue={searchValueV1}
              filterValue={filterValueV1}
              setSearchValue={setSearchValueV1}
              setFilterValue={setFilterValueV1}
              tableVariant="event"
              header="Category"
              marginTop="0"
            />
            {renderEventSections('v1', filteredDataV1(), isTableDataEmptyV1)}
          </AccordionDetails>
        </Accordion>
      )}

      {(hasFeatureFlagEventsV2 || context === ERouteContext.PORTAL) && (
        <Accordion
          sx={{
            boxShadow: 'none',
            borderBottom: '1px solid #E0E0E0',
            '&::before': { display: 'none' }
          }}
        >
          {context !== ERouteContext.PORTAL && <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>
              Events V2
            </Typography>
          </AccordionSummary>}  
          <AccordionDetails>
            <Typography sx={{ mb: 1, fontSize: 14, fontFamily: 'Montserrat' }}>
              Webhook Url:{' '}
              {getIntegration?.data?.eventsV2WebhookUrl || 'https://'}
            </Typography>
            <TableSearchFilters
              searchValue={searchValueV2}
              filterValue={filterValueV2}
              setSearchValue={setSearchValueV2}
              setFilterValue={setFilterValueV2}
              tableVariant="event"
              header="Category"
              marginTop="0"
            />
            {renderEventSections('v2', filteredDataV2(), isTableDataEmptyV2)}
          </AccordionDetails>
        </Accordion>
      )}
      </div>
    </Flex>
  );
};

export default EventsSettings;
